/**
 * features/parent/ParentWelcomeModal.tsx
 * ------------------------------------------------------------------
 * One-time welcome shown the FIRST time a parent opens the portal.
 * "Seen" is persisted on the user doc (users/{uid}.parentWelcomeSeenAt)
 * so it never re-appears — across devices, not just this browser.
 * Parents may write this non-sensitive field on their own doc (rules).
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import { GraduationCap, BookOpen, Calendar, Info, Users, Mail, Check, Loader2 } from 'lucide-react';
import { doc } from 'firebase/firestore';
import { db, updateDoc } from '@/lib/firebase';
import { COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { SCHOOL } from '@/lib/school.config';

export default function ParentWelcomeModal() {
  const { userData } = useAuth();
  const [dismissing, setDismissing] = useState(false);
  const [closedLocally, setClosedLocally] = useState(false);

  // Only for parents who haven't seen it yet.
  if (!userData?.uid || userData.parentWelcomeSeenAt || closedLocally) return null;

  async function dismiss() {
    if (!userData?.uid) return;
    setDismissing(true);
    try {
      await updateDoc(doc(db, COLLECTIONS.users, userData.uid), {
        parentWelcomeSeenAt: new Date().toISOString(),
      });
    } catch {
      /* even if the write fails, don't trap the parent behind the modal */
    } finally {
      setClosedLocally(true);
      setDismissing(false);
    }
  }

  const points = [
    { icon: BookOpen, title: "Follow your child's academic progress", desc: 'View reports and marks as they are published.' },
    { icon: Calendar, title: 'Keep track of attendance', desc: "See your child's attendance record." },
    { icon: Info, title: 'Stay informed', desc: 'Access school information in one place.' },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-2xl bg-white dark:bg-gray-900 shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1b3a6b] to-[#2d5295] text-white px-6 py-6 shrink-0">
          <div className="flex items-center gap-3">
            <div className="bg-white/15 rounded-xl p-2.5">
              <GraduationCap size={24} />
            </div>
            <div>
              <h1 className="text-lg font-bold leading-tight">Welcome to the {SCHOOL.name} Parent Portal</h1>
              <p className="text-sm text-white/80 mt-0.5">We're delighted to have you here.</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4 overflow-y-auto text-sm text-gray-700 dark:text-gray-200">
          <p>
            This is your secure space to stay connected with your child's journey at {SCHOOL.name}.
            From your portal, you can:
          </p>

          <div className="space-y-2.5">
            {points.map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.title} className="flex items-start gap-3 rounded-xl border border-gray-100 dark:border-gray-700 p-3">
                  <div className="mt-0.5 w-8 h-8 rounded-lg bg-[#2d5295]/10 text-[#2d5295] dark:text-blue-300 flex items-center justify-center shrink-0">
                    <Icon size={16} />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">{p.title}</p>
                    <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">{p.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex items-start gap-2.5 rounded-xl bg-blue-50 dark:bg-blue-900/20 p-3">
            <Users size={16} className="text-[#2d5295] dark:text-blue-300 mt-0.5 shrink-0" />
            <p className="text-xs text-gray-600 dark:text-gray-300">
              If you have more than one child at the school, they're all linked to this single
              account — you can switch between them easily.
            </p>
          </div>

          <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 p-3">
            <Mail size={16} className="text-amber-600 dark:text-amber-300 mt-0.5 shrink-0" />
            <p className="text-xs text-gray-600 dark:text-gray-300">
              <span className="font-semibold text-gray-800 dark:text-gray-100">One quick thing:</span> please take a
              moment to check that your contact details are up to date, so we can always reach you. If anything is
              missing, you'll see a prompt to complete it.
            </p>
          </div>

          <p className="text-gray-600 dark:text-gray-300">
            Thank you for partnering with us in your child's education. We're glad you're here.
          </p>
          <p className="text-gray-500 dark:text-gray-400 italic">— {SCHOOL.name}</p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 shrink-0">
          <button
            onClick={dismiss}
            disabled={dismissing}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-[#2d5295] text-white text-sm font-semibold hover:bg-[#1b3a6b] disabled:opacity-60"
          >
            {dismissing ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
            Get started
          </button>
        </div>
      </div>
    </div>
  );
}
